import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Brain, Upload, FileSpreadsheet, Wrench, Plus, X, RefreshCw,
  GripVertical, ChevronDown, ChevronUp, AlertCircle, CheckCircle,
  Download, ArrowLeft, Star, Type, Hash, Calendar,
  Camera, List, MapPin, QrCode, FileSignature, ToggleLeft, Barcode,
} from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

// ── Types ──────────────────────────────────────────────────────────────────
type QuestionType =
  | 'yes_no' | 'yes_no_na' | 'rating_5' | 'rating_10'
  | 'text_short' | 'text_long' | 'number' | 'date' | 'time'
  | 'photo_required' | 'photo_optional' | 'multi_photo'
  | 'checklist' | 'dropdown' | 'signature' | 'gps_location' | 'qr_scan' | 'barcode_scan'

type ScoringMethod = 'weighted' | 'simple' | 'pass_fail'
type RequiresSignature = 'none' | 'auditor' | 'auditee' | 'both'

interface SectionItem {
  question: string
  type: QuestionType
  required: boolean
  photo_required: boolean
  weight: number
  regulation_ref: string
  requirement: string
  options: string[]
  _id: string
}
interface Section {
  title: string
  items: SectionItem[]
  _id: string
}
type ScheduleType = 'none' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'biannual' | 'annual'

const SCHEDULE_DAYS: Record<ScheduleType, number> = {
  none: 0, weekly: 7, fortnightly: 14, monthly: 30, quarterly: 91, biannual: 182, annual: 365,
}

interface TemplateForm {
  name: string
  description: string
  audit_type: string
  frequency: string
  pass_threshold: number
  estimated_duration_mins: number | ''
  scoring_method: ScoringMethod
  requires_signature: RequiresSignature
  applicable_sites: string[]
  sections: Section[]
  // scheduling
  schedule_type: ScheduleType
  schedule_next_due: string
  schedule_assign_to: string
  schedule_site_id: string
}

const AUDIT_TYPES = ['whs', 'environmental', 'quality', 'contractor', 'fire_safety', 'emergency']
const FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly', 'biannual', 'annual', 'ad_hoc']

const QUESTION_TYPES: { value: QuestionType; label: string; icon: React.ReactNode; group: string }[] = [
  { value: 'yes_no',         label: 'Yes / No',           icon: <ToggleLeft size={14} />, group: 'Compliance' },
  { value: 'yes_no_na',      label: 'Yes / No / N/A',     icon: <ToggleLeft size={14} />, group: 'Compliance' },
  { value: 'rating_5',       label: 'Rating 1–5',          icon: <Star size={14} />,        group: 'Rating' },
  { value: 'rating_10',      label: 'Rating 1–10',         icon: <Star size={14} />,        group: 'Rating' },
  { value: 'text_short',     label: 'Short Text',          icon: <Type size={14} />,        group: 'Text' },
  { value: 'text_long',      label: 'Long Text',           icon: <Type size={14} />,        group: 'Text' },
  { value: 'number',         label: 'Number',              icon: <Hash size={14} />,        group: 'Text' },
  { value: 'date',           label: 'Date',                icon: <Calendar size={14} />,    group: 'Text' },
  { value: 'time',           label: 'Time',                icon: <Calendar size={14} />,    group: 'Text' },
  { value: 'photo_required', label: 'Photo (required)',    icon: <Camera size={14} />,      group: 'Media' },
  { value: 'photo_optional', label: 'Photo (optional)',    icon: <Camera size={14} />,      group: 'Media' },
  { value: 'multi_photo',    label: 'Multi-Photo',         icon: <Camera size={14} />,      group: 'Media' },
  { value: 'checklist',      label: 'Checklist',           icon: <List size={14} />,        group: 'Selection' },
  { value: 'dropdown',       label: 'Dropdown Select',     icon: <ChevronDown size={14} />, group: 'Selection' },
  { value: 'signature',      label: 'Signature',           icon: <FileSignature size={14} />, group: 'Special' },
  { value: 'gps_location',   label: 'GPS Location',        icon: <MapPin size={14} />,      group: 'Special' },
  { value: 'qr_scan',        label: 'QR Scan',             icon: <QrCode size={14} />,      group: 'Special' },
  { value: 'barcode_scan',   label: 'Barcode Scan',        icon: <Barcode size={14} />,     group: 'Special' },
]

const uid = () => Math.random().toString(36).slice(2)
const blankItem = (): SectionItem => ({
  question: '', type: 'yes_no_na', required: true, photo_required: false,
  weight: 1, regulation_ref: '', requirement: '', options: [], _id: uid(),
})
const blankSection = (title = ''): Section => ({ title, items: [blankItem()], _id: uid() })
const blankForm = (): TemplateForm => ({
  name: '', description: '', audit_type: 'whs', frequency: 'quarterly',
  pass_threshold: 80, estimated_duration_mins: 60,
  scoring_method: 'weighted', requires_signature: 'none',
  applicable_sites: [],
  sections: [blankSection()],
  schedule_type: 'none',
  schedule_next_due: '',
  schedule_assign_to: '',
  schedule_site_id: '',
})

function sectionsFromAI(aiSections: any[]): Section[] {
  return (aiSections || []).map(sec => ({
    title: sec.title || '',
    _id: uid(),
    items: (sec.items || []).map((it: any) => ({
      question: it.question || '',
      type: (it.type || 'yes_no_na') as QuestionType,
      required: it.required !== false,
      photo_required: !!it.photo_required,
      weight: it.weight || 1,
      regulation_ref: it.regulation_ref || '',
      requirement: it.requirement || '',
      options: it.options || [],
      _id: uid(),
    })),
  }))
}

// ── Drag-and-drop hook for a list ──────────────────────────────────────────
function useDragList<T extends { _id: string }>(
  items: T[],
  onReorder: (newItems: T[]) => void,
) {
  const dragIdx = useRef<number | null>(null)
  const [overIdx, setOverIdx] = useState<number | null>(null)

  const onDragStart = useCallback((i: number) => { dragIdx.current = i }, [])
  const onDragOver = useCallback((e: React.DragEvent, i: number) => {
    e.preventDefault(); setOverIdx(i)
  }, [])
  const onDrop = useCallback((e: React.DragEvent, i: number) => {
    e.preventDefault()
    const from = dragIdx.current
    if (from === null || from === i) { setOverIdx(null); return }
    const next = [...items]
    const [moved] = next.splice(from, 1)
    next.splice(i, 0, moved)
    onReorder(next)
    dragIdx.current = null; setOverIdx(null)
  }, [items, onReorder])
  const onDragEnd = useCallback(() => { dragIdx.current = null; setOverIdx(null) }, [])

  return { onDragStart, onDragOver, onDrop, onDragEnd, overIdx }
}

// ── Question Card ──────────────────────────────────────────────────────────
function QuestionCard({
  item, onUpdate, onRemove, showRemove, dragHandlers, isDragOver,
}: {
  item: SectionItem
  onUpdate: (field: keyof SectionItem, val: any) => void
  onRemove: () => void
  showRemove: boolean
  dragHandlers?: {
    onDragStart: () => void
    onDragOver: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
    onDragEnd: () => void
  }
  isDragOver?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const needsOptions = item.type === 'checklist' || item.type === 'dropdown'

  return (
    <div
      draggable
      onDragStart={dragHandlers?.onDragStart}
      onDragOver={dragHandlers?.onDragOver}
      onDrop={dragHandlers?.onDrop}
      onDragEnd={dragHandlers?.onDragEnd}
      className="rounded-xl border transition-all"
      style={{
        background: 'var(--white)',
        borderColor: isDragOver ? 'var(--accent)' : 'var(--border)',
        boxShadow: isDragOver ? '0 0 0 2px var(--accent)' : undefined,
        opacity: isDragOver ? 0.7 : 1,
      }}
    >
      <div className="flex items-start gap-2 p-3">
        <div className="mt-2.5 flex-shrink-0 cursor-grab active:cursor-grabbing" style={{ color: 'var(--text-muted)' }}>
          <GripVertical size={16} />
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <input
            placeholder="Question text..."
            value={item.question}
            onChange={e => onUpdate('question', e.target.value)}
            className="text-sm"
          />
          <div className="flex gap-2 flex-wrap items-center">
            <select
              value={item.type}
              onChange={e => onUpdate('type', e.target.value as QuestionType)}
              className="text-xs py-1 w-auto"
              style={{ minWidth: 160 }}
            >
              {['Compliance', 'Rating', 'Text', 'Media', 'Selection', 'Special'].map(group => (
                <optgroup key={group} label={group}>
                  {QUESTION_TYPES.filter(t => t.group === group).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={item.required} onChange={e => onUpdate('required', e.target.checked)} className="w-3 h-3" />
              Required
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={item.photo_required} onChange={e => onUpdate('photo_required', e.target.checked)} className="w-3 h-3" />
              Photo
            </label>
            <select value={item.weight} onChange={e => onUpdate('weight', parseInt(e.target.value))} className="text-xs py-1 w-auto">
              <option value={1}>Weight: 1</option>
              <option value={2}>Weight: 2</option>
              <option value={3}>Weight: 3 (critical)</option>
            </select>
            <button
              type="button"
              onClick={() => setExpanded(x => !x)}
              className="text-xs flex items-center gap-0.5"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)' }}
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {expanded ? 'Less' : 'More'}
            </button>
          </div>
          {expanded && (
            <div className="space-y-2 pt-1">
              <input
                placeholder="Regulation reference (e.g. WHS Reg 2017 r.44)"
                value={item.regulation_ref}
                onChange={e => onUpdate('regulation_ref', e.target.value)}
                className="text-xs"
              />
              <input
                placeholder="Compliance requirement — what does a passing response look like?"
                value={item.requirement}
                onChange={e => onUpdate('requirement', e.target.value)}
                className="text-xs"
              />
              {needsOptions && (
                <div>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Options (one per line)</p>
                  <textarea
                    rows={3}
                    className="text-xs"
                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                    value={item.options.join('\n')}
                    onChange={e => onUpdate('options', e.target.value.split('\n'))}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {showRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="mt-2 flex-shrink-0"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

// ── Section Builder ────────────────────────────────────────────────────────
function SectionEditor({
  sec, si, totalSections, onUpdateTitle, onRemoveSection,
  onAddItem, onRemoveItem, onUpdateItem, onReorderItems,
  regenButton,
}: {
  sec: Section
  si: number
  totalSections: number
  onUpdateTitle: (val: string) => void
  onRemoveSection: () => void
  onAddItem: () => void
  onRemoveItem: (qi: number) => void
  onUpdateItem: (qi: number, field: keyof SectionItem, val: any) => void
  onReorderItems: (items: SectionItem[]) => void
  regenButton?: React.ReactNode
}) {
  const { onDragStart, onDragOver, onDrop, onDragEnd, overIdx } = useDragList(sec.items, onReorderItems)

  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <div className="flex gap-2 items-center">
        <input
          placeholder={`Section ${si + 1} title`}
          value={sec.title}
          onChange={e => onUpdateTitle(e.target.value)}
          className="flex-1 font-medium"
        />
        {regenButton}
        {totalSections > 1 && (
          <button type="button" onClick={onRemoveSection} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        )}
      </div>
      <div className="space-y-2">
        {sec.items.map((item, qi) => (
          <QuestionCard
            key={item._id}
            item={item}
            onUpdate={(f, v) => onUpdateItem(qi, f, v)}
            onRemove={() => onRemoveItem(qi)}
            showRemove={sec.items.length > 1}
            isDragOver={overIdx === qi}
            dragHandlers={{
              onDragStart: () => onDragStart(qi),
              onDragOver: (e) => onDragOver(e, qi),
              onDrop: (e) => onDrop(e, qi),
              onDragEnd,
            }}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={onAddItem}
        className="flex items-center gap-1 text-xs"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)' }}
      >
        <Plus size={12} /> Add question
      </button>
    </div>
  )
}

// ── Full Section List Editor ───────────────────────────────────────────────
function SectionsEditor({
  form, setForm, showRegenFor, onRegen, regenLoadingIdx,
}: {
  form: TemplateForm
  setForm: React.Dispatch<React.SetStateAction<TemplateForm>>
  showRegenFor?: Set<number>
  onRegen?: (si: number) => void
  regenLoadingIdx?: number | null
}) {
  const updateSection = (si: number, field: keyof Section, val: any) =>
    setForm(f => ({ ...f, sections: f.sections.map((s, i) => i === si ? { ...s, [field]: val } : s) }))
  const removeSection = (si: number) =>
    setForm(f => ({ ...f, sections: f.sections.filter((_, i) => i !== si) }))
  const addItem = (si: number) =>
    setForm(f => ({ ...f, sections: f.sections.map((s, i) => i === si ? { ...s, items: [...s.items, blankItem()] } : s) }))
  const removeItem = (si: number, qi: number) =>
    setForm(f => ({ ...f, sections: f.sections.map((s, i) => i === si ? { ...s, items: s.items.filter((_, j) => j !== qi) } : s) }))
  const updateItem = (si: number, qi: number, field: keyof SectionItem, val: any) =>
    setForm(f => ({
      ...f, sections: f.sections.map((s, i) => i === si ? {
        ...s, items: s.items.map((it, j) => j === qi ? { ...it, [field]: val } : it),
      } : s),
    }))
  const reorderItems = (si: number, items: SectionItem[]) =>
    setForm(f => ({ ...f, sections: f.sections.map((s, i) => i === si ? { ...s, items } : s) }))

  const questionCount = form.sections.reduce((n, s) => n + s.items.filter(it => it.question.trim()).length, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {form.sections.length} section{form.sections.length !== 1 ? 's' : ''} · {questionCount} question{questionCount !== 1 ? 's' : ''} · drag grip to reorder questions
        </p>
      </div>
      {form.sections.map((sec, si) => (
        <SectionEditor
          key={sec._id}
          sec={sec}
          si={si}
          totalSections={form.sections.length}
          onUpdateTitle={val => updateSection(si, 'title', val)}
          onRemoveSection={() => removeSection(si)}
          onAddItem={() => addItem(si)}
          onRemoveItem={qi => removeItem(si, qi)}
          onUpdateItem={(qi, f, v) => updateItem(si, qi, f, v)}
          onReorderItems={items => reorderItems(si, items)}
          regenButton={showRegenFor?.has(si) && onRegen ? (
            <button
              type="button"
              onClick={() => onRegen(si)}
              disabled={regenLoadingIdx === si}
              className="flex items-center gap-1 text-xs btn btn-secondary py-1 px-2 flex-shrink-0"
            >
              <RefreshCw size={11} className={regenLoadingIdx === si ? 'animate-spin' : ''} />
              {regenLoadingIdx === si ? 'Regenerating…' : 'Regenerate'}
            </button>
          ) : undefined}
        />
      ))}
      <button
        type="button"
        onClick={() => setForm(f => ({ ...f, sections: [...f.sections, blankSection()] }))}
        className="btn btn-secondary text-sm flex items-center gap-1"
      >
        <Plus size={14} /> Add Section
      </button>
    </div>
  )
}

// ── Schedule Section ───────────────────────────────────────────────────────
function ScheduleSection({
  form, setForm, users, sites,
}: {
  form: TemplateForm
  setForm: React.Dispatch<React.SetStateAction<TemplateForm>>
  users: { id: string; first_name: string; last_name: string }[]
  sites: { id: string; name: string }[]
}) {
  const enabled = form.schedule_type !== 'none'

  const handleTypeChange = (val: ScheduleType) => {
    setForm(f => ({ ...f, schedule_type: val }))
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Auto-Schedule</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Automatically create audit instances on a recurring schedule</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div
            onClick={() => setForm(f => ({ ...f, schedule_type: enabled ? 'none' : 'monthly' }))}
            className="relative transition-all"
            style={{
              width: 44, height: 24, borderRadius: 12,
              background: enabled ? 'var(--accent)' : 'var(--border)',
              cursor: 'pointer',
            }}
          >
            <div style={{
              position: 'absolute', top: 3, left: enabled ? 23 : 3,
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s',
            }} />
          </div>
          <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      </div>

      {enabled && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Recurrence</label>
            <select value={form.schedule_type} onChange={e => handleTypeChange(e.target.value as ScheduleType)}>
              <option value="weekly">Weekly (every 7 days)</option>
              <option value="fortnightly">Fortnightly (every 14 days)</option>
              <option value="monthly">Monthly (every 30 days)</option>
              <option value="quarterly">Quarterly (every 91 days)</option>
              <option value="biannual">Biannual (every 182 days)</option>
              <option value="annual">Annual (every 365 days)</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>First Due Date</label>
            <input
              type="date"
              value={form.schedule_next_due}
              min={new Date().toISOString().slice(0, 10)}
              onChange={e => setForm(f => ({ ...f, schedule_next_due: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Assign To <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
            <select value={form.schedule_assign_to} onChange={e => setForm(f => ({ ...f, schedule_assign_to: e.target.value }))}>
              <option value="">— unassigned —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Site <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
            <select value={form.schedule_site_id} onChange={e => setForm(f => ({ ...f, schedule_site_id: e.target.value }))}>
              <option value="">— all sites —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {form.schedule_next_due && (
            <div className="col-span-2 text-xs rounded-lg px-3 py-2" style={{ background: 'var(--accent-light, #EBF2FF)', color: 'var(--accent)' }}>
              Next audit will be auto-created on {new Date(form.schedule_next_due + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })},
              then every {SCHEDULE_DAYS[form.schedule_type]} days thereafter.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Template Settings Panel ────────────────────────────────────────────────
function TemplateSettings({
  form, setForm, sites,
}: {
  form: TemplateForm
  setForm: React.Dispatch<React.SetStateAction<TemplateForm>>
  sites: { id: string; name: string }[]
}) {
  const toggleSite = (id: string) => {
    setForm(f => ({
      ...f,
      applicable_sites: f.applicable_sites.includes(id)
        ? f.applicable_sites.filter(s => s !== id)
        : [...f.applicable_sites, id],
    }))
  }

  return (
    <div className="card space-y-4">
      <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Template Settings</h2>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Template Name *</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Quarterly WHS Field Operations Audit" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Audit Type</label>
          <select value={form.audit_type} onChange={e => setForm(f => ({ ...f, audit_type: e.target.value }))}>
            {AUDIT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Frequency</label>
          <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
            {FREQUENCIES.map(fr => <option key={fr} value={fr}>{fr.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Pass Threshold (%)</label>
          <input type="number" min={0} max={100} value={form.pass_threshold} onChange={e => setForm(f => ({ ...f, pass_threshold: parseInt(e.target.value) || 80 }))} />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Estimated Duration (mins)</label>
          <input type="number" min={5} max={480} value={form.estimated_duration_mins} onChange={e => setForm(f => ({ ...f, estimated_duration_mins: parseInt(e.target.value) || '' }))} />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Scoring Method</label>
          <select value={form.scoring_method} onChange={e => setForm(f => ({ ...f, scoring_method: e.target.value as ScoringMethod }))}>
            <option value="weighted">Weighted (question weights apply)</option>
            <option value="simple">Simple (all questions equal)</option>
            <option value="pass_fail">Pass / Fail only</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Requires Signature</label>
          <select value={form.requires_signature} onChange={e => setForm(f => ({ ...f, requires_signature: e.target.value as RequiresSignature }))}>
            <option value="none">None</option>
            <option value="auditor">Auditor only</option>
            <option value="auditee">Auditee only</option>
            <option value="both">Both — auditor & auditee</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description</label>
          <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description of this template's purpose and scope..." />
        </div>
        {sites.length > 0 && (
          <div className="col-span-2">
            <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>
              Applicable Sites <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank for all)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {sites.map(s => {
                const active = form.applicable_sites.includes(s.id)
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleSite(s.id)}
                    className="text-xs px-3 py-1.5 rounded-full transition-all"
                    style={{
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      background: active ? 'var(--accent)' : 'var(--white)',
                      color: active ? '#fff' : 'var(--text)',
                    }}
                  >
                    {s.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Template Builder ─────────────────────────────────────────────────
type Method = 'ai' | 'document' | 'excel' | 'manual'

export default function AuditTemplateBuilder() {
  const navigate = useNavigate()
  const accessToken = useAuthStore(s => s.accessToken)
  const [method, setMethod] = useState<Method>('ai')
  const [form, setForm] = useState<TemplateForm>(blankForm())
  const [sites, setSites] = useState<{ id: string; name: string }[]>([])
  const [users, setUsers] = useState<{ id: string; first_name: string; last_name: string }[]>([])

  // AI state
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState('')
  const [aiGenerated, setAiGenerated] = useState(false)
  const [regenIdx, setRegenIdx] = useState<number | null>(null)

  // Document state
  const [docFile, setDocFile] = useState<File | null>(null)
  const [docLoading, setDocLoading] = useState(false)
  const [docError, setDocError] = useState('')
  const [docGenerated, setDocGenerated] = useState(false)
  const [docDragOver, setDocDragOver] = useState(false)
  const docInputRef = useRef<HTMLInputElement>(null)

  // Excel state
  const [xlFile, setXlFile] = useState<File | null>(null)
  const [xlLoading, setXlLoading] = useState(false)
  const [xlError, setXlError] = useState('')
  const [xlWarnings, setXlWarnings] = useState<string[]>([])
  const [xlGenerated, setXlGenerated] = useState(false)
  const xlInputRef = useRef<HTMLInputElement>(null)

  // Save state
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    api.get('/sites').then(r => setSites(r.data?.data || r.data || [])).catch(() => {})
    api.get('/users').then(r => setUsers(r.data?.data || r.data || [])).catch(() => {})
  }, [])

  const applyTemplate = (tmpl: any) => {
    setForm(f => ({
      ...f,
      name: tmpl.name || f.name,
      description: tmpl.description || f.description,
      audit_type: tmpl.audit_type || f.audit_type,
      frequency: tmpl.frequency || f.frequency,
      pass_threshold: tmpl.pass_threshold ?? f.pass_threshold,
      estimated_duration_mins: tmpl.estimated_duration_mins || f.estimated_duration_mins,
      scoring_method: tmpl.scoring_method || f.scoring_method,
      requires_signature: tmpl.requires_signature || f.requires_signature,
      sections: sectionsFromAI(tmpl.sections || []),
    }))
  }

  // ── AI generate ──────────────────────────────────────────────────────────
  const generateAI = async () => {
    if (!aiPrompt.trim()) { setAiError('Please describe the audit you want to create'); return }
    setAiLoading(true); setAiError('')
    try {
      const { data } = await api.post('/audits/templates/ai-generate', {
        description: aiPrompt,
        audit_type: form.audit_type,
        frequency: form.frequency,
        pass_threshold: form.pass_threshold,
      })
      applyTemplate(data.template)
      setAiGenerated(true)
    } catch (err: any) {
      setAiError(err.response?.data?.error || 'AI generation failed — check your API key configuration')
    }
    setAiLoading(false)
  }

  const regenerateSection = async (si: number) => {
    if (!aiPrompt.trim()) return
    setRegenIdx(si)
    try {
      const sec = form.sections[si]
      const { data } = await api.post('/audits/templates/ai-generate', {
        description: `Regenerate only the "${sec.title}" section of this audit: ${aiPrompt}`,
        audit_type: form.audit_type,
      })
      const newSecs = sectionsFromAI(data.template?.sections || [])
      if (newSecs.length > 0) {
        setForm(f => ({
          ...f, sections: f.sections.map((s, i) => i === si
            ? { ...newSecs[0], title: sec.title, _id: s._id }
            : s),
        }))
      }
    } catch {}
    setRegenIdx(null)
  }

  // ── Document extract ─────────────────────────────────────────────────────
  const handleDocDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDocDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) setDocFile(file)
  }

  const extractDocument = async () => {
    if (!docFile) { setDocError('Please select a file'); return }
    setDocLoading(true); setDocError('')
    try {
      const fd = new FormData()
      fd.append('file', docFile)
      fd.append('audit_type', form.audit_type)
      fd.append('frequency', form.frequency)
      fd.append('pass_threshold', String(form.pass_threshold))
      const { data } = await api.post('/audits/templates/from-document', fd)
      applyTemplate(data.template)
      setDocGenerated(true)
    } catch (err: any) {
      setDocError(err.response?.data?.error || 'Document extraction failed')
    }
    setDocLoading(false)
  }

  // ── Excel ─────────────────────────────────────────────────────────────────
  const downloadExcel = async () => {
    const res = await fetch('/api/audits/templates/excel-blank', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'audit-template-import.xlsx'; a.click()
    URL.revokeObjectURL(url)
  }

  const parseExcel = async () => {
    if (!xlFile) { setXlError('Please select an Excel file'); return }
    setXlLoading(true); setXlError(''); setXlWarnings([])
    try {
      const fd = new FormData()
      fd.append('file', xlFile)
      fd.append('audit_type', form.audit_type)
      fd.append('frequency', form.frequency)
      fd.append('pass_threshold', String(form.pass_threshold))
      const { data } = await api.post('/audits/templates/from-excel', fd)
      applyTemplate(data.template)
      setXlGenerated(true)
      if (data.errors?.length) setXlWarnings(data.errors.map((e: any) => `Row ${e.row}: ${e.message}`))
    } catch (err: any) {
      setXlError(err.response?.data?.error || 'Excel parse failed')
    }
    setXlLoading(false)
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async () => {
    if (!form.name.trim()) { setSaveError('Template name is required'); return }
    const validSections = form.sections.filter(s => s.title.trim() && s.items.some(it => it.question.trim()))
    if (!validSections.length) { setSaveError('At least one section with one question is required'); return }
    setSaving(true); setSaveError('')
    try {
      await api.post('/audits/templates', {
        name: form.name,
        description: form.description || undefined,
        audit_type: form.audit_type,
        frequency: form.frequency,
        pass_threshold: form.pass_threshold,
        estimated_duration_mins: form.estimated_duration_mins || undefined,
        scoring_method: form.scoring_method,
        requires_signature: form.requires_signature,
        applicable_sites: form.applicable_sites.length ? form.applicable_sites : undefined,
        schedule_type: form.schedule_type,
        schedule_interval_days: form.schedule_type !== 'none' ? SCHEDULE_DAYS[form.schedule_type] : undefined,
        schedule_next_due: form.schedule_type !== 'none' && form.schedule_next_due ? form.schedule_next_due : undefined,
        schedule_assign_to: form.schedule_assign_to || undefined,
        schedule_site_id: form.schedule_site_id || undefined,
        sections: validSections.map(s => ({
          title: s.title,
          items: s.items
            .filter(it => it.question.trim())
            .map(({ _id, ...rest }) => rest),
        })),
      })
      navigate('/audits/templates')
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Failed to save template')
    }
    setSaving(false)
  }

  const hasContent = form.sections.some(s => s.items.some(it => it.question.trim()))
  const questionCount = form.sections.reduce((n, s) => n + s.items.filter(it => it.question.trim()).length, 0)

  const METHOD_TABS = [
    { id: 'ai' as Method,       label: 'AI Prompt',       icon: <Brain size={18} />,         desc: 'Describe in plain English' },
    { id: 'document' as Method, label: 'Upload Document',  icon: <Upload size={18} />,        desc: 'PDF, Word, or photo' },
    { id: 'excel' as Method,    label: 'Excel Import',     icon: <FileSpreadsheet size={18} />, desc: 'Download, fill, upload' },
    { id: 'manual' as Method,   label: 'Manual Builder',   icon: <Wrench size={18} />,        desc: 'Drag & drop questions' },
  ]

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/audits/templates')} className="btn btn-secondary p-2">
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>New Audit Template</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>AI prompt · document extraction · Excel import · manual builder</p>
        </div>
        {hasContent && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'var(--green-light, #f0fdf4)', color: 'var(--green)', border: '1px solid var(--green)' }}>
              {questionCount} questions ready
            </span>
          </div>
        )}
      </div>

      {/* Template settings */}
      <TemplateSettings form={form} setForm={setForm} sites={sites} />

      {/* Schedule settings */}
      <ScheduleSection form={form} setForm={setForm} users={users} sites={sites} />

      {/* Method selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {METHOD_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setMethod(tab.id)}
            className="p-3 rounded-xl text-left transition-all"
            style={{
              border: `2px solid ${method === tab.id ? 'var(--accent)' : 'var(--border)'}`,
              background: method === tab.id ? 'var(--accent-light, #EBF2FF)' : 'var(--white)',
            }}
          >
            <div className="flex items-center gap-2 mb-1" style={{ color: method === tab.id ? 'var(--accent)' : 'var(--text-secondary)' }}>
              {tab.icon}
              <span className="text-xs font-semibold">{tab.label}</span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{tab.desc}</p>
          </button>
        ))}
      </div>

      {/* ── Method 1: AI Prompt ── */}
      {method === 'ai' && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2">
            <Brain size={20} style={{ color: 'var(--purple)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>AI Prompt Builder</h2>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Describe the audit in plain English. Claude generates sections, questions, types, weights and Australian regulatory references.
          </p>
          <textarea
            rows={4}
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            placeholder='e.g. "Monthly WHS audit for electrical meter installation teams working in residential and commercial premises. Focus on electrical safety, PPE, LOTO, working at heights and documentation compliance under NSW WHS Regulation 2017."'
          />
          {aiError && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{aiError}
            </div>
          )}
          <div className="flex gap-3 items-center flex-wrap">
            <button type="button" onClick={generateAI} className="btn btn-primary flex items-center gap-2" disabled={aiLoading}>
              <Brain size={16} /> {aiLoading ? 'Generating…' : aiGenerated ? 'Regenerate All' : 'Generate Template'}
            </button>
            {aiGenerated && (
              <span className="text-xs flex items-center gap-1" style={{ color: 'var(--green)' }}>
                <CheckCircle size={14} /> Generated — review and edit sections below
              </span>
            )}
          </div>
          <p className="text-xs ai-label">AI-generated — review all questions before saving (NSW Digital Work Systems Act 2026)</p>
        </div>
      )}

      {/* ── Method 2: Document Upload ── */}
      {method === 'document' && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2">
            <Upload size={20} style={{ color: 'var(--accent)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Document Upload</h2>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Upload a PDF, Word (.docx), or photo of a paper form / whiteboard. Claude Vision extracts all questions and sections and converts them to a digital template.
          </p>
          <div
            className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all"
            style={{
              borderColor: docDragOver ? 'var(--accent)' : docFile ? 'var(--green)' : 'var(--border)',
              background: docDragOver ? 'var(--accent-light, #EBF2FF)' : docFile ? 'var(--green-light, #f0fdf4)' : 'var(--bg)',
            }}
            onClick={() => docInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDocDragOver(true) }}
            onDragLeave={() => setDocDragOver(false)}
            onDrop={handleDocDrop}
          >
            <Upload size={32} className="mx-auto mb-2" style={{ color: docFile ? 'var(--green)' : 'var(--text-muted)' }} />
            {docFile ? (
              <div>
                <p className="font-medium text-sm" style={{ color: 'var(--green)' }}>{docFile.name}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{(docFile.size / 1024).toFixed(0)} KB · click to change</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Click to upload or drag & drop</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>PDF · DOCX · JPG · PNG — max 10 MB</p>
              </div>
            )}
          </div>
          <input
            ref={docInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.docx,.jpg,.jpeg,.png,.gif,.webp"
            onChange={e => { if (e.target.files?.[0]) setDocFile(e.target.files[0]) }}
          />
          {docError && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{docError}
            </div>
          )}
          <div className="flex gap-3 items-center">
            <button type="button" onClick={extractDocument} className="btn btn-primary flex items-center gap-2" disabled={docLoading || !docFile}>
              <Brain size={16} /> {docLoading ? 'Extracting…' : 'Extract Template from Document'}
            </button>
            {docGenerated && (
              <span className="text-xs flex items-center gap-1" style={{ color: 'var(--green)' }}>
                <CheckCircle size={14} /> Extracted — review and edit below
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Method 3: Excel ── */}
      {method === 'excel' && (
        <div className="card space-y-5">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={20} style={{ color: 'var(--green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Excel Import</h2>
          </div>

          <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent)', color: '#fff' }}>1</span>
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Download the Excel template</p>
            </div>
            <p className="text-sm ml-8" style={{ color: 'var(--text-muted)' }}>
              Pre-filled with instructions, example rows and a Question Types reference sheet. Columns: Section · Question · Type · Required · Photo · Weight · Regulation.
            </p>
            <div className="ml-8">
              <button type="button" onClick={downloadExcel} className="btn btn-secondary flex items-center gap-2">
                <Download size={16} /> Download Excel Template (.xlsx)
              </button>
            </div>
          </div>

          <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent)', color: '#fff' }}>2</span>
              <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Fill in your questions offline, then upload</p>
            </div>
            <div
              className="ml-8 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer"
              style={{ borderColor: xlFile ? 'var(--green)' : 'var(--border)', background: xlFile ? 'var(--green-light, #f0fdf4)' : 'var(--white)' }}
              onClick={() => xlInputRef.current?.click()}
            >
              <FileSpreadsheet size={28} className="mx-auto mb-2" style={{ color: xlFile ? 'var(--green)' : 'var(--text-muted)' }} />
              {xlFile
                ? <p className="text-sm font-medium" style={{ color: 'var(--green)' }}>{xlFile.name}</p>
                : <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Click to upload completed Excel file (.xlsx)</p>}
            </div>
            <input ref={xlInputRef} type="file" className="hidden" accept=".xlsx,.xls" onChange={e => { if (e.target.files?.[0]) setXlFile(e.target.files[0]) }} />

            {xlWarnings.length > 0 && (
              <div className="p-3 rounded-lg space-y-1" style={{ background: 'var(--amber-light, #fffbeb)', border: '1px solid var(--amber)' }}>
                {xlWarnings.map((w, i) => <p key={i} className="text-xs" style={{ color: 'var(--amber)' }}>⚠ {w}</p>)}
              </div>
            )}
            {xlError && (
              <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{xlError}
              </div>
            )}
            <div className="flex gap-3 items-center">
              <button type="button" onClick={parseExcel} className="btn btn-primary flex items-center gap-2" disabled={xlLoading || !xlFile}>
                <FileSpreadsheet size={16} /> {xlLoading ? 'Parsing…' : 'Import Questions from Excel'}
              </button>
              {xlGenerated && <span className="text-xs flex items-center gap-1" style={{ color: 'var(--green)' }}><CheckCircle size={14} /> Imported — review below</span>}
            </div>
          </div>
        </div>
      )}

      {/* ── Sections editor ── */}
      {(method === 'manual' || hasContent) && (
        <div className="card space-y-4">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>
            {method === 'manual' ? 'Question Builder' : 'Review & Edit Sections'}
          </h2>
          <SectionsEditor
            form={form}
            setForm={setForm}
            showRegenFor={method === 'ai' && aiGenerated ? new Set(form.sections.map((_, i) => i)) : undefined}
            onRegen={method === 'ai' ? regenerateSection : undefined}
            regenLoadingIdx={regenIdx}
          />
        </div>
      )}

      {/* ── Save bar ── */}
      {(method === 'manual' || hasContent) && (
        <div className="card space-y-3">
          {saveError && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{saveError}
            </div>
          )}
          <div className="flex gap-3 items-center">
            <button onClick={save} className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : `Save Template${questionCount > 0 ? ` (${questionCount} questions)` : ''}`}
            </button>
            <button onClick={() => navigate('/audits/templates')} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
